/**
 * PlanTimeline — Vertical CSS timeline for plan change history.
 * No external animation library — pure CSS + Tailwind.
 */

import React from "react";
import { ArrowUpRight, ArrowDownRight, AlertTriangle, RefreshCw } from "lucide-react";
import { cn } from "../../lib/utils";
import type { PlanChangeEvent, PlanName } from "../../api/subscriptions";

interface PlanTimelineProps {
  history: PlanChangeEvent[];
}

function isUpgrade(from: PlanName, to: PlanName): "upgrade" | "downgrade" | "same" {
  const rank: Record<PlanName, number> = { free: 0, starter: 1, growth: 2, enterprise: 3 };
  if (rank[to] > rank[from]) return "upgrade";
  if (rank[to] < rank[from]) return "downgrade";
  return "same";
}

const EVENT_ICON: Record<"upgrade" | "downgrade" | "same", React.ReactNode> = {
  upgrade: <ArrowUpRight className="h-3.5 w-3.5 text-green-400" />,
  downgrade: <ArrowDownRight className="h-3.5 w-3.5 text-amber-400" />,
  same: <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />,
};

const DOT_COLOR: Record<"upgrade" | "downgrade" | "same", string> = {
  upgrade: "bg-green-500 ring-green-500/30",
  downgrade: "bg-amber-500 ring-amber-500/30",
  same: "bg-slate-400 ring-slate-400/20",
};

const PLAN_LABEL: Record<PlanName, string> = {
  free: "Community",
  starter: "Starter",
  growth: "Growth",
  enterprise: "Enterprise",
};

function triggerLabel(changedBy: string, paystackEvent: string | null): string {
  if (paystackEvent) return paystackEvent.replace(/_/g, " ");
  if (changedBy === "admin") return "Admin change";
  if (changedBy === "system") return "System change";
  return changedBy;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function PlanTimeline({ history }: PlanTimelineProps) {
  if (history.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-12 text-center">
        <RefreshCw className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">No plan changes yet.</p>
        <p className="text-xs text-muted-foreground/60">
          History will appear here after your first plan change.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Vertical connector line */}
      <div className="absolute left-3.5 top-4 bottom-4 w-px bg-border/50" />

      <ol className="space-y-6 pl-10">
        {history.map((event, idx) => {
          const dir = isUpgrade(event.from_plan, event.to_plan);
          return (
            <li key={event.id} className="relative">
              {/* Dot */}
              <div
                className={cn(
                  "absolute -left-[26px] top-1 h-3 w-3 rounded-full ring-4",
                  DOT_COLOR[dir]
                )}
              />

              <div className="rounded-xl border border-border/60 bg-surface1/60 p-4 hover:bg-surface1 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    {EVENT_ICON[dir]}
                    <span className="text-sm font-semibold text-foreground">
                      {PLAN_LABEL[event.from_plan]}{" "}
                      <span className="text-muted-foreground font-normal">→</span>{" "}
                      {PLAN_LABEL[event.to_plan]}
                    </span>
                    {dir === "upgrade" && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-green-500/15 text-green-600 dark:text-green-400 uppercase tracking-wide">
                        Upgrade
                      </span>
                    )}
                    {dir === "downgrade" && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 uppercase tracking-wide">
                        Downgrade
                      </span>
                    )}
                  </div>
                  <time className="text-[11px] text-muted-foreground whitespace-nowrap flex-shrink-0">
                    {formatDate(event.changed_at)}
                  </time>
                </div>

                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs text-muted-foreground">
                    Trigger:{" "}
                    <span className="text-foreground/70 italic">
                      {triggerLabel(event.changed_by, event.paystack_event)}
                    </span>
                  </span>
                  {event.notes && (
                    <>
                      <span className="text-muted-foreground/40">·</span>
                      <span className="text-xs text-muted-foreground/70 italic">{event.notes}</span>
                    </>
                  )}
                </div>

                {dir === "downgrade" && idx === 0 && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                    <span>Most recent downgrade — data retention window may be active.</span>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
